import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Typography,
  Button,
  ProgressStepper,
  Step,
  StepButton,
  StepLabel,
} from '@one-thd/sui-atomic-components';
import { RadioButtonUnchecked, InfoFilled, ConfirmedFilled } from '@one-thd/sui-icons';
import { defaultCompletedDrawerDescription, defaultPostOptInDescription } from '../../../../../core/Constants';
import { SweepstakesMarkdown } from '../../../utils/SweepstakesMarkdown';
import { windowExists } from '../../../../../common/utils/AccountUtils';
import { useImageError, parsePrerequisiteSteps, arePrerequisitesMet } from '../../../utils/SweepstakesUtils';
import { RenderCompletionAlert } from '../../SweepstakesUtils';
import { fireTrackYourEntriesButtonAnalytics, fireDrawerOpenButtonAnalytics } from '../../../utils/SweepstakesAnalyticsUtils';
import { SWEEPSTAKES_CONSTANTS } from '../../../../../core/Constants';

// Utility functions
const handleStepClick = (step, stepIndex, sweepstakesData) => {
  if (windowExists()) {
    fireTrackYourEntriesButtonAnalytics(step, stepIndex, sweepstakesData?.steps);
    fireDrawerOpenButtonAnalytics(
      SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.STEP,
      { ...step, stepIndex },
      sweepstakesData?.steps
    );
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('loyalty-benefits.open-sweepstakes-step-drawer', {
      selectedStep: {
        ...step,
        stepIndex,
      },
      sweepstakesData,
    });
  }
};

const handleFirstStepClick = (sweepstakesData) => {
  if (windowExists()) {
    fireTrackYourEntriesButtonAnalytics(sweepstakesData?.steps?.[0], 0, sweepstakesData?.steps);
    fireDrawerOpenButtonAnalytics(
      SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.SURVEY, null, sweepstakesData?.steps
    );
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('loyalty-benefits.open-sweepstakes-survey-drawer', {
      sweepstakesData,
    });
  }
};

// Render components
const RenderStepButton = ({
  step, index, steps, isOptedIn, onStepClick, onFirstStepClick, hasCompletedSurvey, sweepstakesEnded,
  inactiveStepTrackerIcon
}) => {
  const stepIcon = useImageError();
  const defaultWhiteIcon = inactiveStepTrackerIcon || 'https://placehold.co/28x28/FFFFFF/FFF';
  const isFirstStep = index === 0;

  const prerequisiteSteps = parsePrerequisiteSteps(step?.stepMetaData?.prerequisitessteps);
  const prerequisitesMet = prerequisiteSteps.length > 0
    ? arePrerequisitesMet(prerequisiteSteps, steps)
    : true;

  // Determine if button should be clickable
  // First step: clickable only if opted in and not completed
  // Other steps: clickable only if prerequisites are met (if provided)
  const isClickable = isFirstStep
    ? (isOptedIn && !step.completed)
    : prerequisitesMet;

  const clickFunction = isFirstStep ? () => onFirstStepClick(step) : () => onStepClick(step, index);
  const handleClick = isClickable ? clickFunction : undefined;
  const btnCTAs = ['Enter Now', 'Watch Now', 'Shop Now', 'Get Started'];
  const ctaLabel = step?.stepMetaData?.ctaDescription || btnCTAs[index] || 'Get Started';
  const disabled = index !== 0 && !hasCompletedSurvey;
  const isComplete = step?.completed;

  return (
    <div className="sui-flex sui-w-full sui-justify-between sui-my-1">
      <Step key={step.id} completed={step.completed}>
        <div data-testid={`step-button-${step.id}`} style={{ pointerEvents: 'none' }}>
          <StepButton
            onClick={handleClick}
            icon={() => (
              stepIcon.hasError ? (
                <RadioButtonUnchecked />
              ) : (
                <img
                  src={step.completed ? step.iconUrl : defaultWhiteIcon}
                  alt={`Step ${index + 1}`}
                  width={28}
                  height={28}
                  className="sui-rounded-full"
                  onError={stepIcon.handleError}
                />
              )
            )}
          />
        </div>
        <StepLabel>
          <div
            className={`${isClickable ? 'sui-cursor-pointer' : ''} sui-flex sui-justify-between`}
            data-testid={`step-label-${step.id}`}
          >
            <Typography
              variant="body-base"
              weight="bold"
            >
              <SweepstakesMarkdown markdownText={step.title} testId={`sweepstakes-step-title-${index}`} />
            </Typography>
            {step.subtitle && !step.completed && (
              <div>
                <Typography variant="body-sm" color="subtle">
                  {step.subtitle}
                </Typography>
              </div>
            )}
          </div>
        </StepLabel>
      </Step>
      {!isComplete && !sweepstakesEnded && (
        <div className="sui-w-auto sui-ml-1 sui-text-nowrap">
          <Button
            variant="tertiary"
            size="small"
            onClick={handleClick}
            disabled={disabled}
          >
            {ctaLabel}
          </Button>
        </div>
      )}
    </div>
  );
};

const RenderStepsSection = ({
  steps,
  isOptedIn,
  onStepClick,
  onFirstStepClick,
  sweepstakesEnded,
  inactiveStepTrackerIcon
}) => {
  const stepperRef = React.useRef(null);

  React.useEffect(() => {
    const list = stepperRef.current?.querySelector('ol');
    /**
     * Experiences are overriding/omitting certain styling for elements like ol.
     * We need to use JS to dynamically apply styles in this situation
     */
    if (list) {
      list.classList.add('sui-w-full', 'sui-min-w-0', 'sui-flex', 'sui-flex-col');
      list.classList.remove('sui-min-w-min'); // Remove any conflicting classes from the ProgressStepper
    }
  }, []);

  if (!steps || steps.length === 0) return null;

  const hasCompletedSurvey = steps[0]?.completed;

  return (
    <div>
      <ProgressStepper
        centerEdgeNodes
        nonLinear
        orientation="vertical"
        connector={<></>}
        className="sui-w-full"
        ref={stepperRef}
      >
        {steps.map((step, index) => (
          <RenderStepButton
            key={step.id}
            step={step}
            index={index}
            steps={steps}
            isOptedIn={isOptedIn}
            onStepClick={onStepClick}
            onFirstStepClick={onFirstStepClick}
            hasCompletedSurvey={hasCompletedSurvey}
            sweepstakesEnded={sweepstakesEnded}
            inactiveStepTrackerIcon={inactiveStepTrackerIcon}
          />
        ))}
      </ProgressStepper>
    </div>
  );
};

const RenderFooter = ({ sweepstakesData }) => {
  if (!sweepstakesData?.ctaTitle) return null;

  return (
    <Button
      variant="primary"
      fullWidth
      data-testid="sweepstakes-details-cta-button"
    >
      {sweepstakesData.ctaTitle}
    </Button>
  );
};

export const DetailsContent = ({ sweepstakesData }) => {
  const onStepClick = React.useCallback((step, stepIndex) => {
    handleStepClick(step, stepIndex, sweepstakesData);
  }, [sweepstakesData]);

  const onFirstStepClick = React.useCallback(() => {
    handleFirstStepClick(sweepstakesData);
  }, [sweepstakesData]);

  const {
    drawerDescription,
    completedDrawerDescription,
    steps,
    isOptedIn,
    completedSteps,
    totalSteps,
    expirationDate,
    sweepstakesEndedDescription,
    displayChallengeUntil,
    stepTrackerPostOptInDescription,
    activityStatus,
    challengeStatus,
    inactiveStepTrackerIcon,
  } = sweepstakesData || {};

  const isComplete = completedSteps === totalSteps && totalSteps > 0;
  const isChallengeExpired = challengeStatus === 'EXPIRED';

  const parsedEndDate = expirationDate ? new Date(expirationDate) : null;
  const isSweepstakesEnded = parsedEndDate && !Number.isNaN(parsedEndDate.getTime())
    ? Date.now() > parsedEndDate.getTime()
    : false;

  const showEndedAlert = (isComplete && isSweepstakesEnded) || isChallengeExpired;

  let description = drawerDescription;

  if (isChallengeExpired) {
    description = null;
  } else if (isComplete) {
    description = isSweepstakesEnded
      ? null
      : (completedDrawerDescription || defaultCompletedDrawerDescription);
  } else if (isOptedIn && completedSteps === 0) {
    description = stepTrackerPostOptInDescription || defaultPostOptInDescription;
  }

  const content = (
    <div className="sui-space-y-6" data-testid="sweepstakes-details-content">
      {showEndedAlert && sweepstakesEndedDescription && (
        <Alert status="info">
          <Typography>
            {displayChallengeUntil
              ? `${sweepstakesEndedDescription} by ${displayChallengeUntil}.`
              : sweepstakesEndedDescription}
          </Typography>
        </Alert>
      )}
      {description && (
        <div>
          <SweepstakesMarkdown markdownText={description} />
        </div>
      )}
      <RenderStepsSection
        steps={steps}
        isOptedIn={isOptedIn}
        onStepClick={onStepClick}
        onFirstStepClick={onFirstStepClick}
        sweepstakesEnded={showEndedAlert}
        inactiveStepTrackerIcon={inactiveStepTrackerIcon}
      />
      {!isChallengeExpired && <RenderCompletionAlert />}
    </div>
  );

  const footer = <RenderFooter sweepstakesData={sweepstakesData} />;

  return { content, footer };
};

DetailsContent.propTypes = {
  sweepstakesData: PropTypes.shape({
    title: PropTypes.string,
    logo: PropTypes.string,
    ctaTitle: PropTypes.string,
    completedSteps: PropTypes.number,
    totalSteps: PropTypes.number,
    allEntriesCompletedText: PropTypes.string,
    expirationDate: PropTypes.string,
    sweepstakesEndedDescription: PropTypes.string,
    displayChallengeUntil: PropTypes.string,
    activityStatus: PropTypes.string,
    challengeStatus: PropTypes.string,
    inactiveStepTrackerIcon: PropTypes.string,
    termsAndConditions: PropTypes.string,
    stepTrackerLearnMoreText: PropTypes.string,
    steps: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      subtitle: PropTypes.string,
      iconUrl: PropTypes.string,
      completed: PropTypes.bool,
      stepMetaData: PropTypes.shape({}),
    })),
  }),
};

DetailsContent.defaultProps = {
  sweepstakesData: null,
};
